import { FC } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { useAppSelector } from '@hooks/store';
import { PageContent, UnableToLoadData } from '@components/index';
import strings from '@localizedstrings/LocalisedStrings';
import './adviser.scss';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { ScreenState } from '@src/app/enums';
import { Box, Button, Divider, Typography } from '@mui/material';
import { Business, PhoneIphoneOutlined, WorkspacePremiumOutlined } from '@mui/icons-material';

const AdviserPage: FC = (): JSX.Element => {
  const { adviser, logoUrl, portfolioInfoState } = useAppSelector(selectPortfolios);

  return (
    <Container maxWidth={false}>
      <Box display="flex" flexDirection="column" gap="24px" mb={6}>
        <Box display="flex" gap="8px" alignItems="center">
          <WorkspacePremiumOutlined fontSize="large" />
          <Typography fontWeight={600} fontSize="30px">
            Adviser
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="sm">
        <PageContent isLoading={portfolioInfoState === ScreenState.Loading}>
          {!adviser ? (
            <UnableToLoadData />
          ) : (
            <Grid container direction="column" spacing={2} justifyContent="center">
              {logoUrl && (
                <Grid item alignSelf="center">
                  <Box
                    component="img"
                    src={logoUrl}
                    alt="Logo"
                    sx={{
                      height: '100%',
                      maxHeight: '100px',
                      width: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </Grid>
              )}
              <Grid item alignSelf="center">
                <Typography fontWeight={600} fontSize="20px">{`${adviser?.name} (Adviser)`}</Typography>
              </Grid>
              {adviser?.afsl && (
                <Grid item container spacing={1} alignItems="center" justifyContent="center">
                  <Grid item mt={0.5}>
                    <Business fontSize="small" sx={{ color: '#646464' }} />
                  </Grid>
                  <Grid item>
                    <Typography>{adviser?.afsl.name}</Typography>
                  </Grid>
                </Grid>
              )}
              {adviser?.emailAddress && (
                <Grid item container spacing={1} alignItems="center" justifyContent="center" flexWrap="nowrap" sx={{ maxWidth: '100%' }}>
                  <Grid item mt={1}>
                    <MailOutlineOutlinedIcon fontSize="small" sx={{ color: '#646464' }} />
                  </Grid>
                  <Grid item sx={{ maxWidth: '80%' }}>
                    <Typography
                      noWrap
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        display: 'block',
                      }}
                    >
                      {adviser?.emailAddress}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {adviser?.mobilePhoneNumber && (
                <Grid item container spacing={1} alignItems="center" justifyContent="center">
                  <Grid item mt={0.5}>
                    <PhoneIphoneOutlined fontSize="small" sx={{ color: '#646464' }} />
                  </Grid>
                  <Grid item>
                    <Typography>{adviser?.mobilePhoneNumber}</Typography>
                  </Grid>
                </Grid>
              )}
              {(adviser?.emailAddress || adviser?.calendarUrl) && (
                <Grid item>
                  <Divider />
                </Grid>
              )}

              <Grid item container spacing={2}>
                {adviser?.emailAddress && (
                  <Grid item>
                    <Button sx={{ backgroundColor: '#F2FAF8', padding: '8px' }} href={`mailto:${adviser?.emailAddress}`} target="_self">
                      {strings.email}
                    </Button>
                  </Grid>
                )}
                {adviser?.calendarUrl && (
                  <Grid item>
                    <Button sx={{ backgroundColor: '#F2FAF8', padding: '8px' }} target="_blank" href={adviser?.calendarUrl}>
                      {strings.scheduleAppointment}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </PageContent>
      </Container>
    </Container>
  );
};

export default AdviserPage;
